import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getEffectiveTooltips,
  parseTooltipEffectiveResponse,
} from "./tooltipApi";
import {
  flattenTooltipCatalog,
  getStaticTooltipMap,
  productCodesForRuntime,
  tooltipLookupKey,
} from "./tooltipCatalog";
import {
  clearEffectiveTooltipTexts,
  getEffectiveTooltip,
  setEffectiveTooltipTexts,
  t as resolveT,
} from "./tooltipRuntimeCache";

type TooltipContextValue = {
  ready: boolean;
  refresh: () => Promise<void>;
  getTooltip: (groupKey: string, fieldKey: string, fallback?: string) => string;
  t: (groupKey: string, fieldKey: string, fallback?: string) => string;
};

const TooltipContext = createContext<TooltipContextValue>({
  ready: false,
  refresh: async () => undefined,
  getTooltip: getEffectiveTooltip,
  t: resolveT,
});

type TooltipProviderProps = {
  productName: string;
  children: React.ReactNode;
};

/**
 * Loads effective tooltip copy for the active product after login.
 * On API failure, static TooltipInformation.ts defaults remain in effect.
 */
export function TooltipProvider({
  productName,
  children,
}: TooltipProviderProps) {
  const [ready, setReady] = useState(false);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(async () => {
    const codes = productCodesForRuntime(productName);
    const collected: { groupKey: string; fieldKey: string; text: string }[] =
      [];

    // Seed cache with static defaults first so UI never blanks on slow/failed GET
    const staticMap = getStaticTooltipMap();
    staticMap.forEach((text, key) => {
      const [groupKey, fieldKey] = key.split("::");
      if (groupKey && fieldKey) {
        collected.push({ groupKey, fieldKey, text });
      }
    });

    try {
      const responses = await Promise.all(
        codes.map((code) => getEffectiveTooltips(code)),
      );
      for (const response of responses) {
        const parsed = await parseTooltipEffectiveResponse(response);
        if (!parsed) continue;
        for (const item of parsed.items) {
          if (!item.groupKey || !item.fieldKey) continue;
          const idx = collected.findIndex(
            (c) =>
              c.groupKey === item.groupKey && c.fieldKey === item.fieldKey,
          );
          if (idx >= 0) {
            collected[idx] = {
              groupKey: item.groupKey,
              fieldKey: item.fieldKey,
              text: item.text || collected[idx].text,
            };
          } else {
            collected.push({
              groupKey: item.groupKey,
              fieldKey: item.fieldKey,
              text: item.text,
            });
          }
        }
      }
      setEffectiveTooltipTexts(collected);
    } catch {
      // Keep static fallbacks already in collected
      setEffectiveTooltipTexts(collected);
    } finally {
      setReady(true);
      setVersion((v) => v + 1);
    }
  }, [productName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
      clearEffectiveTooltipTexts();
    };
  }, [refresh]);

  const value = useMemo<TooltipContextValue>(
    () => ({
      ready,
      refresh,
      getTooltip: getEffectiveTooltip,
      t: resolveT,
    }),
    // version bumps when cache refreshes so consumers re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, refresh, version],
  );

  return (
    <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
  );
}

export function useTooltips(): TooltipContextValue {
  return useContext(TooltipContext);
}

export function useTooltipText(
  groupKey: string,
  fieldKey: string,
  fallback?: string,
): string {
  const { ready } = useTooltips();
  void ready;
  return getEffectiveTooltip(groupKey, fieldKey, fallback);
}

/** Ensure catalog is warm for seed callers without side effects. */
export function preloadTooltipCatalogSize(): number {
  return flattenTooltipCatalog().length;
}

export { tooltipLookupKey, resolveT as t };
